import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, signOut as fbSignOut, type User } from 'firebase/auth';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { useQueryClient } from '@tanstack/react-query';
import { auth, db } from '../../lib/firebase';
import { readClaims } from '../../lib/claims';
import type { OrgClaims, OrgId, Role, UserDoc } from '../../types';

/**
 * Who is signed in, what they can do, and which org they are looking at.
 *
 * Claims are the authorisation, and they live in the ID token. The token
 * refreshes about once an hour on its own, which is far too slow for a role
 * change to take effect, so this watches `/users/{uid}.claimsUpdatedAt` — which
 * the claims trigger stamps — and forces a refresh the moment it moves.
 */

const ORG_STORAGE_KEY = 'trackilero.orgId';

export interface AuthState {
  status: 'loading' | 'signed-out' | 'signed-in';
  user: User | null;
  profile: UserDoc | null;
  claims: OrgClaims;
  /** The org currently being viewed. Null until one is chosen. */
  orgId: OrgId | null;
  role: Role | null;
  /** Suspended orgs are readable and exportable, and reject every write. */
  isSuspended: boolean;
  canWrite: boolean;
  isAdmin: boolean;
  isManagerUp: boolean;
  /** Owner, admin or accountant: sees rates, invoices and pay. */
  isFinance: boolean;
  /** Reads everything they are allowed to read and writes nothing. */
  isViewer: boolean;
  /** Outside the company: their own time and the work in front of them. */
  isContractor: boolean;
  /** Inside the company and able to write: shapes the work, not only does it. */
  isStaff: boolean;
  setOrgId: (orgId: OrgId) => void;
  signOut: () => Promise<void>;
}

const emptyClaims: OrgClaims = { orgs: {}, susp: [], superAdmin: false };

const AuthContext = createContext<AuthState>({
  status: 'loading',
  user: null,
  profile: null,
  claims: emptyClaims,
  orgId: null,
  role: null,
  isSuspended: false,
  canWrite: false,
  isAdmin: false,
  isManagerUp: false,
  isFinance: false,
  isViewer: false,
  isContractor: false,
  isStaff: false,
  setOrgId: () => undefined,
  signOut: async () => undefined,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserDoc | null>(null);
  const [claims, setClaims] = useState<OrgClaims>(emptyClaims);
  const [orgId, setOrgIdState] = useState<OrgId | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, async (next) => {
      setUser(next);
      if (!next) {
        setProfile(null);
        setClaims(emptyClaims);
        setOrgIdState(null);
        setStatus('signed-out');
        queryClient.clear();
        return;
      }
      setClaims(await readClaims(next));
      setStatus('signed-in');
    });
  }, [queryClient]);

  // The claims watcher. Without it, a person granted a role keeps being told
  // they lack permission until their token happens to expire.
  useEffect(() => {
    if (!user) return;
    let lastSeen = 0;

    return onSnapshot(doc(db, 'users', user.uid), async (snapshot) => {
      const data = snapshot.data() as UserDoc | undefined;
      if (!data) {
        // A brand-new account has no profile yet: the server only writes one
        // when the person creates or joins an organisation, and the root
        // route will not send them there until a profile says they have none.
        // Rules allow exactly this create — own uid, no orgs.
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email ?? '',
          displayName: user.displayName || user.email || '',
          photoUrl: null,
          emailVerified: user.emailVerified,
          orgIds: [],
          defaultOrgId: null,
          notificationOptOut: false,
          claimsUpdatedAt: null,
          createdAt: serverTimestamp(),
          lastSeenAt: null,
        }).catch(() => undefined);
        return;
      }
      setProfile(data);

      const millis = data.claimsUpdatedAt?.toMillis?.() ?? 0;
      if (millis <= lastSeen) return;
      lastSeen = millis;

      setClaims(await readClaims(user, true));
      // Anything cached was read under the old permissions.
      queryClient.invalidateQueries();
    });
  }, [user, queryClient]);

  // Pick an org: the last one used if it is still valid, otherwise the user's
  // default, otherwise the first they belong to.
  useEffect(() => {
    const available = Object.keys(claims.orgs);
    if (available.length === 0) {
      setOrgIdState(null);
      return;
    }
    setOrgIdState((current) => {
      if (current && available.includes(current)) return current;
      const remembered = localStorage.getItem(ORG_STORAGE_KEY);
      if (remembered && available.includes(remembered)) return remembered;
      if (profile?.defaultOrgId && available.includes(profile.defaultOrgId)) {
        return profile.defaultOrgId;
      }
      return available[0]!;
    });
  }, [claims, profile]);

  const value = useMemo<AuthState>(() => {
    const role = orgId ? (claims.orgs[orgId] ?? null) : null;
    const isSuspended = !!orgId && claims.susp.includes(orgId);
    return {
      status,
      user,
      profile,
      claims,
      orgId,
      role,
      isSuspended,
      canWrite: !!role && !isSuspended && role !== 'viewer',
      isAdmin: role === 'owner' || role === 'admin',
      isManagerUp: role === 'owner' || role === 'admin' || role === 'manager',
      isFinance: role === 'owner' || role === 'admin' || role === 'accountant',
      isViewer: role === 'viewer',
      isContractor: role === 'contractor',
      isStaff: !!role && !isSuspended && role !== 'viewer' && role !== 'contractor',
      setOrgId: (next) => {
        localStorage.setItem(ORG_STORAGE_KEY, next);
        setOrgIdState(next);
      },
      signOut: async () => {
        localStorage.removeItem(ORG_STORAGE_KEY);
        await fbSignOut(auth);
      },
    };
  }, [status, user, profile, claims, orgId]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/**
 * For the many hooks that only make sense with an org selected. Throwing here
 * beats every caller handling a null it cannot do anything about.
 */
export function useOrgId(): OrgId {
  const { orgId } = useAuth();
  if (!orgId) throw new Error('useOrgId called outside an organisation context');
  return orgId;
}
